import { Button, Modal, Spinner } from "@xola/ui-kit";
import React, { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { ExportRosterFilters, JobStatus, RosterExportJob } from "types";
import { api } from "../../api/api";
import { useResource } from "../../hooks/useResource";
import { useAccountStore } from "../../stores/accountStore";
import { openAuthenticatedUrl } from "../../utils/url";
import { NoTranslate } from "../Common/NoTranslate";

interface ExportRosterModalProps {
    eventId?: ID[] | ID;
    isOpen: boolean;
    onClose: () => void;
    filters: ExportRosterFilters;
}

export const ExportRosterModal = ({ eventId, isOpen, onClose, filters }: ExportRosterModalProps) => {
    return (
        <Modal isLoading isOpen={isOpen} onClose={onClose}>
            <ModalContent eventId={eventId} filters={filters} onClose={onClose} />
        </Modal>
    );
};

interface ModalContentProps {
    eventId?: ID[] | ID;
    onClose: () => void;
    filters: ExportRosterFilters;
}

// TODO: Refactor to use `ExportJobsModal.tsx`
const ModalContent = ({ eventId, onClose, filters }: ModalContentProps) => {
    const [enableFetchRoster, setEnableFetchRoster] = useState(false);
    const [refetchInterval, setRefetchInterval] = useState<number | false>(1000);
    const seller = useAccountStore((state) => state.seller);

    const { data: exportJob } = useQuery<RosterExportJob>(["events", filters], async () => fetchRoster(filters), {
        enabled: enableFetchRoster,
        staleTime: 0,
    });

    const { data: job } = useResource<RosterExportJob>(["jobs", exportJob?.id], null, {
        refetchInterval,
        staleTime: 0,
        onSuccess: (job) => {
            if (job.status !== JobStatus.NEW && job.status !== JobStatus.PROCESSING) {
                setRefetchInterval(false);
            }

            if (job.status === JobStatus.COMPLETED && job.url) {
                window.open(job.url);
            }
        },
    });

    useEffect(() => {
        if (eventId) {
            if (Array.isArray(eventId) && eventId.length > 0) {
                setEnableFetchRoster(true);
            } else {
                const params = new URLSearchParams(filters as any).toString();
                openAuthenticatedUrl(`/events/${eventId}?seller=${seller}&${params}`);
                onClose();
            }
        } else {
            setEnableFetchRoster(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <Modal.Header>Generating Report</Modal.Header>

            <Modal.Body>
                {!job || job?.status === JobStatus.NEW ? (
                    <div className="space-y-4">
                        <div>
                            <strong>Status:</strong> Waiting in Queue <Spinner className="ml-1" size="tiny" />
                        </div>

                        <p>
                            We have received your export request, it is waiting in queue to be processed. You can keep
                            this window open, or close it and we&apos;ll email you a link to the report.
                        </p>
                    </div>
                ) : job?.status === JobStatus.PROCESSING ? (
                    <div className="space-y-4">
                        <div>
                            <strong>Status:</strong> Processing <Spinner className="ml-2" size="tiny" />
                        </div>

                        <p>
                            We&apos;re generating your report now. It&apos;ll just take a moment. You can keep this
                            window open, or close it and we&apos;ll email you a link to the report.
                        </p>

                        {job?.processedCount && job?.processedCount > 0 ? (
                            <p>
                                Records Processed: <NoTranslate>{job.processedCount}</NoTranslate>
                            </p>
                        ) : null}
                    </div>
                ) : job?.status === JobStatus.COMPLETED ? (
                    <div className="space-y-4">
                        <div>
                            <strong>Status:</strong> Export Complete
                        </div>

                        <p>
                            Your report is ready! It should have already started downloading, but if it hasn&apos;t, you
                            can{" "}
                            <a className="text-primary hover:underline" target="_blank" href={job.url} rel="noreferrer">
                                download it here
                            </a>
                            . We&apos;ve also sent you an email with the download link.
                        </p>
                    </div>
                ) : job?.status === JobStatus.STALE || job?.status === JobStatus.ERROR ? (
                    <div className="space-y-4">
                        <div>
                            <strong>Status:</strong> Error
                        </div>

                        <p>
                            Oops, Xola encountered an error while trying to generating your report. Please contact Xola
                            Support.
                        </p>

                        <p>
                            Job ID: <NoTranslate>{job.id}</NoTranslate>
                        </p>
                    </div>
                ) : null}
            </Modal.Body>

            <Modal.Footer>
                <Button color="primary" onClick={onClose}>
                    Close
                </Button>
            </Modal.Footer>
        </>
    );
};

const fetchRoster = async (filters: ExportRosterFilters) => {
    const { data } = await api.get<RosterExportJob>("/events", { params: filters });
    return data;
};
